<template>
  <div class="deposit-account-groups-form">
    <a-spin :spinning="loading" :tip="labels.form.loading">
      <ValidationObserver ref="observer">
        <a-form
          :model="form"
          :label-width="80"
          slot-scope="{ validate }"
          @submit.prevent="validate().then(handleSubmit)"
        >
          <a-row
            type="flex"
            justify="space-between"
            :gutter="16"
            :style="{ marginBottom: '15px' }"
          >
            <!-- Title -->
            <a-col :xs="24" :md="12">
              <h2 class="form-title">
                <a-icon :type="isEdit ? 'edit' : 'form'" class="form-icon" />
                <span>{{
                  isEdit
                    ? `${labels.form.titleView} número: ${form.code || ""}`
                    : labels.form.titleCreate
                }}</span>
              </h2>
            </a-col>
            <!-- Actions -->
            <a-col :xs="24" :md="12">
              <a-row
                type="flex"
                justify="end"
                :gutter="16"
                :style="{
                  alignItems: 'flex-end',
                  marginBottom: '0px',
                }"
              >
                <a-col>
                  <a-button-group>
                    <a-button
                      :type="isEdit ? 'primary' : 'danger'"
                      ghost
                      @click="handleBack"
                      icon="arrow-left"
                    >
                      <span>{{ labels.form.goBack }}</span>
                    </a-button>
                    <a-button
                      v-if="isEdit && canDelete"
                      type="danger"
                      @click="confirmDelete"
                    >
                      <span>{{ labels.form.delete }}</span>
                    </a-button>
                    <a-button
                      v-if="!isMobile && canEdit"
                      type="primary"
                      ghost
                      :loading="submitting"
                      @click="validate().then(() => handleSubmit(true))"
                    >
                      <span>{{ labels.form.saveAndClose }}</span>
                    </a-button>
                    <a-button
                      v-if="canEdit"
                      type="primary"
                      :loading="submitting"
                      @click="validate().then(handleSubmit)"
                    >
                      <span>{{ labels.form.save }}</span>
                    </a-button>
                    <a-dropdown :trigger="['click']" v-if="isMobile && canEdit">
                      <a-menu slot="overlay">
                        <a-menu-item
                          key="1"
                          @click="validate().then(() => handleSubmit(true))"
                          >{{ labels.form.saveAndClose }}</a-menu-item
                        >
                      </a-menu>
                      <a-button type="primary" ghost icon="more" />
                    </a-dropdown>
                  </a-button-group>
                </a-col>
              </a-row>
            </a-col>
          </a-row>
          <a-row type="flex" justify="center">
            <a-col :span="24">
              <a-card>
                <a-row type="flex" justify="space-between">
                  <a-col :xs="20" :sm="20" :md="8">
                    <Input
                      v-model="form.name"
                      :vid="labels.form.name.label"
                      :label="labels.form.name.label"
                      :name="labels.form.name.label"
                      :formitem="{ label: labels.form.name.label }"
                      :placeholder="labels.form.name.placeholder"
                      rules="required"
                      :readOnly="!canEdit"
                      :allowClear="true"
                    />
                  </a-col>
                  <a-col>
                    <SwitchComponent
                      v-model="form.status"
                      :vid="labels.form.status.label"
                      :showLabel="true"
                      :label="labels.form.status.label"
                      :name="labels.form.status.label"
                      :formitem="{ label: labels.form.status.label }"
                      :disabled="!canEdit"
                    />
                  </a-col>
                </a-row>
                <a-row type="flex" justify="space-between">
                  <a-col :span="24">
                    <Input
                      v-model="form.link"
                      :vid="labels.form.link.label"
                      :label="labels.form.link.label"
                      :name="labels.form.link.label"
                      :formitem="{ label: labels.form.link.label }"
                      :placeholder="labels.form.link.placeholder"
                      :readOnly="!canEdit"
                      :allowClear="true"
                    />
                  </a-col>
                </a-row>
                <!-- DIVIDER -->
                <a-row type="flex" justify="end">
                  <a-col :span="24">
                    <a-divider orientation="left">{{
                      labels.form.accounts.label
                    }}</a-divider>
                  </a-col>
                  <a-col>
                    <a-button
                      type="primary"
                      icon="plus"
                      :loading="loading"
                      @click="open = true"
                      class="btn-add"
                      ghost
                      block
                    >
                      <span>{{ labels.form.add }}</span>
                    </a-button>
                  </a-col>
                </a-row>

                <!-- ACCOUNTS -->
                <a-row
                  type="flex"
                  justify="space-between"
                  :gutter="24"
                  class="border-bottom"
                >
                  <a-col :span="24">
                    <div
                      class="deposit-account-list-container"
                      v-if="form.accounts?.length"
                    >
                      <div
                        class="deposit-account-list-item"
                        v-for="(account, key) in form.accounts"
                        :key="key"
                      >
                        <div class="deposit-account-list-item-controls">
                          <a-button
                            type="link"
                            icon="edit"
                            size="small"
                            class="edit-btn"
                            @click="handleEdit({ text: account, index: key })"
                            :title="labels.card.actions.edit"
                          />
                          <a-button
                            type="link"
                            icon="delete"
                            size="small"
                            class="delete-btn"
                            @click="handleDelete({ text: account, index: key })"
                            style="color: red"
                            :title="labels.card.actions.delete"
                          />
                        </div>
                        <span v-html="account" />
                      </div>
                    </div>
                    <a-empty
                      v-else
                      :description="labels.card.emptyText"
                      :image="Empty.PRESENTED_IMAGE_SIMPLE"
                    />
                  </a-col>
                </a-row>
              </a-card>
            </a-col>
          </a-row>
        </a-form>
      </ValidationObserver>
    </a-spin>

    <!-- ADD ACCOUNT MODAL -->
    <a-modal
      :visible="open"
      @cancel="handleCancel"
      :width="isMobile ? '100%' : 600"
    >
      <template #title>
        <a-icon type="plus" />
        {{ labels.form.addModalTitle }}
      </template>
      <ValidationObserver ref="formObserver">
        <a-form
          :model="form"
          :label-width="80"
          slot-scope="{ validate }"
          @submit.prevent="validate().then(handleAdd)"
        >
          <a-row type="flex" justify="start">
            <a-col :span="24">
              <a-form-item :label="labels.form.account.label">
                <wysiwyg v-model="formModal.text" :minHeight="600" />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
      </ValidationObserver>
      <template #footer>
        <a-button type="danger" @click="handleCancel">{{
          labels.form.cancel
        }}</a-button>
        <a-button type="primary" @click="handleAdd">
          {{ labels.form.save }}
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import labels from "@/utils/labels";
import Input from "@/components/core/VeeValidateForm/Input.vue";
import SwitchComponent from "@/components/core/VeeValidateForm/Switch.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import router from "@/router";
import * as actions from "@/utils/actions";
import { mask } from "vue-the-mask";
import { adminPageTitle, hasRoles, roles } from "../../../utils/utils";
import { Empty } from "ant-design-vue";

const initialFormState = {
  name: "",
  accounts: [],
  status: true,
  link: "",
};

const defaultForm = {
  index: undefined,
  text: "",
};

export default {
  name: "DepositAccountGroupForm",
  directives: { themask: mask },
  components: {
    ValidationObserver,
    Input,
    SwitchComponent,
  },
  metaInfo: {
    title: adminPageTitle(labels.depositAccountGroups.title),
    titleTemplate: () => {
      return `${adminPageTitle()} - ${
        router.currentRoute.name.includes("view")
          ? labels.depositAccountGroups.pageTitleViewing
          : labels.depositAccountGroups.pageTitleCreate
      }`;
    },
  },
  data() {
    return {
      labels: labels.depositAccountGroups,
      loading: false,
      submitting: false,
      open: false,
      form: _.cloneDeep(initialFormState),
      formModal: _.cloneDeep(defaultForm),
      Empty,
    };
  },
  async created() {
    this.resetForm();
    if (this.$route.params.id) {
      this.fetchData();
    }
  },
  computed: {
    ...mapState("depositAccountGroups", ["depositAccountGroup"]),
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    isEdit() {
      return !!this.$route.params?.id;
    },
    canEdit() {
      const { accessRoles } = this.getLoggedUser;
      return (
        !this.isEdit ||
        hasRoles(accessRoles, [roles.admin.ROLE_DEPOSIT_ACCOUNT_GROUPS_UPDATE])
      );
    },
    canDelete() {
      const { accessRoles } = this.getLoggedUser;
      return hasRoles(accessRoles, [
        roles.admin.ROLE_DEPOSIT_ACCOUNT_GROUPS_DELETE,
      ]);
    },
    isMobile() {
      return this.screenWidth < 768;
    },
  },
  methods: {
    ...mapActions("depositAccountGroups", [
      "fetchDepositAccountGroup",
      "createDepositAccountGroup",
      "updateDepositAccountGroup",
      "deleteDepositAccountGroup",
      "resetForm",
      "fetchDepositAccountGroups",
    ]),
    resetModalForm() {
      this.formModal = _.cloneDeep(defaultForm);
      this.$refs.formObserver.reset();
    },
    async fetchData() {
      try {
        this.loading = true;
        await this.fetchDepositAccountGroup(this.$route.params.id);
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
        this.$log.error(error);
        this.$router.push({ name: "admin.deposit-account-groups" });
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit(closeAfterSave = false) {
      if (this.submitting) return;
      this.submitting = true;
      let response = null;
      let editting = this.isEdit;
      try {
        const formData = _.cloneDeep(this.form);
        Object.assign(formData, {
          accounts: formData.accounts.filter((account) => !!account?.trim()),
        });

        response = editting
          ? await this.updateDepositAccountGroup(formData)
          : await this.createDepositAccountGroup(formData);

        if (response) this.$message.success(response.message);
        if (closeAfterSave) {
          this.$router.push({ name: "admin.deposit-account-groups" });
        } else {
          if (!editting) {
            this.$router.push({
              name: "admin.deposit-account-groups.edit",
              params: { id: response.data.id },
            });
          }
        }
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
      } finally {
        this.submitting = false;
      }
    },
    confirmDelete() {
      this.$confirm({
        title: this.labels.confirm.title,
        content: this.labels.confirm.subtitle,
        okText: this.labels.confirm.confirmButtonText,
        okType: "danger",
        icon: "close-circle",
        cancelText: this.labels.confirm.cancelButtonText,
        onOk: async () => {
          try {
            await this.deleteDepositAccountGroup(this.form.id);
            this.$store.commit(
              `depositAccountGroups/${actions.GET_COMMISSION_PROFILE}`,
              null
            );
            this.$message.success(this.labels.deletedSuccess);
            this.$router.push({ name: "admin.deposit-account-groups" });
          } catch (error) {
            this.$message.error(
              error?.response?.data?.message ||
                error?.response?.message ||
                error.message ||
                this.labels.deletedError
            );
          }
        },
      });
    },
    handleBack() {
      this.$router.push({ name: "admin.deposit-account-groups" });
    },
    async handleAdd() {
      try {
        const isValid = await this.$refs.formObserver.validate();
        if (!isValid) return;

        const {
          formModal: { index = null, text },
        } = this;

        if (index !== null) {
          this.form.accounts.splice(index, 1, text);
        } else {
          this.form.accounts.push(text);
        }

        this.open = false;
        this.resetModalForm();
      } catch (error) {
        console.error(error);
      }
    },
    handleDelete(record) {
      const { accounts = [] } = this.form;
      accounts.splice(record.index, 1);
    },
    handleCancel() {
      this.open = false;
      this.resetModalForm();
    },
    handleEdit(record) {
      this.formModal = _.cloneDeep(record);
      this.open = true;
    },
  },
  watch: {
    "$route.params.id"(value, oldValue) {
      if (value && value !== oldValue) {
        this.fetchData();
      }
    },
    depositAccountGroup: {
      handler(depositAccountGroup) {
        Object.assign(
          this.form,
          depositAccountGroup
            ? {
                ..._.cloneDeep(depositAccountGroup),
              }
            : _.cloneDeep(initialFormState)
        );
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.form = _.cloneDeep(initialFormState);
    this.resetForm();
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.deposit-account-groups-form {
  .form-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .form-icon {
    margin-right: 8px;
  }
  .form-button-group {
    margin-top: 16px;
  }
  .type-label {
    margin-bottom: 0;
    margin-top: 5px;
  }
  .deposit-account-list-container {
    min-width: 100%;
    max-width: 100%;
    display: flex;
    gap: 10px;
    justify-content: flex-start;
    flex-wrap: wrap;
    .deposit-account-list-item {
      display: inline-block;
      width: 240px;
      padding: 12px 15px;
      border: 1px solid color(border-color);
      box-shadow: 0 2px 4px 0 color(border-color);
      border-radius: 5px;
      position: relative;
      &-controls {
        text-align: right;
        position: absolute;
        top: 8px;
        right: 8px;
        .edit-btn {
          color: color(primary);
          font-size: 16px;
          margin-right: 10px;
        }
        .delete-btn {
          color: color(error);
          font-size: 16px;
        }
      }
    }
  }
}
::v-deep {
  .ant-input[disabled],
  .ant-time-picker-input[disabled],
  .ant-input-number-disabled,
  .ant-select-disabled .ant-select-selection {
    background-color: color(--white) !important;
    color: rgba(0, 0, 0, 0.65) !important;
  }
}
</style>
